import {
  FluidRowData,
  FluidSortOrder,
  FluidTableAction,
  FluidTableGroupedDataset,
  FluidTableHeader,
  sentenceCaseToSnakeCase,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { forceUpdate } from '@stencil/core';

import {
  EXPANDED,
  GROUP_EXPANDED,
  GROUP_ID,
  ID,
  VISIBLE,
} from '../../../constants/constants';
import { Utils } from '../../../utils/utils';

const hasGroupHeaderMutation = (header: FluidTableHeader) =>
  header.grouping && header.grouping.headerValue;

export const TABLE_GROUP_ID = (
  group: FluidTableGroupedDataset,
  idx?: number
): string => {
  const groupIdentifier = group?.groupedByValue || idx || generateUniqueId();
  return `${sentenceCaseToSnakeCase(groupIdentifier)}_group`;
};
const groupHeader = (
  headers: FluidTableHeader[],
  group: FluidTableGroupedDataset,
  idx: number
) => {
  return headers.reduce(
    (groupHeader: FluidRowData, current: FluidTableHeader) => {
      groupHeader[current.dataPath] = hasGroupHeaderMutation(current)
        ? current.grouping.headerValue(group.groupedData)
        : undefined;
      return {
        ...groupHeader,
        [GROUP_ID]: TABLE_GROUP_ID(group, idx),
        [VISIBLE]: true,
        [GROUP_EXPANDED]: group.groupConfig?.collapsedIf
          ? !group.groupConfig?.collapsedIf(group.groupedByValue)
          : true,
      };
    },
    {} as FluidRowData
  );
};

export const isGroupedTable = (headers: FluidTableHeader[]) =>
  headers.some((h) => h.grouped);

export const isAdditionalHeaderTable = (headers: FluidTableHeader[]) =>
  headers.some((h) => h.grouping?.additionalHeaderRow);

export const sortByGroupHeaderValue = (
  groupedDataSets: FluidTableGroupedDataset[],
  dataPath: string,
  sortDirection: FluidSortOrder
) => {
  return groupedDataSets.sort(
    (a: FluidTableGroupedDataset, b: FluidTableGroupedDataset) => {
      return sortDirection === FluidSortOrder.ASC
        ? a.groupHeader?.[dataPath] > b.groupHeader?.[dataPath]
          ? 1
          : -1
        : a.groupHeader?.[dataPath] > b.groupHeader?.[dataPath]
        ? -1
        : 1;
    }
  );
};

export const findGroup = (host, groupId) =>
  host._groupedDataSource.find((g) => g.groupHeader[ID] === groupId);
export const isExpanded = (host, groupId) =>
  findGroup(host, groupId)?.groupHeader[EXPANDED];
export const groupedByValue = (host, groupId) =>
  findGroup(host, groupId)?.groupedByValue;

export const toggleExpand = (groupId: string, host) => {
  host._groupedDataSource = host._groupedDataSource.map((g) => ({
    ...g,
    groupHeader: {
      ...g.groupHeader,
      [EXPANDED]:
        g.groupHeader[ID] === groupId
          ? !g.groupHeader[EXPANDED]
          : g.groupHeader[EXPANDED],
    },
  }));
  host.groupToggled.emit({
    groupedByValue: groupedByValue(host, groupId),
    collapsed: !isExpanded(host, groupId),
  });
  forceUpdate(host);
};

export const collectGroupCollapsedState = (host) => {
  return host._groupedDataSource.reduce(
    (state: { [key: string]: boolean }, current: FluidTableGroupedDataset) => {
      state[current.groupedByValue] = !current.groupHeader[EXPANDED];
      return state;
    },
    {}
  );
};

export const groupDataSource = (
  headers: FluidTableHeader[],
  dataItems: FluidRowData[]
) => {
  const start = performance.now();
  const groupedByHeader = headers.find((h) => h.grouped);
  if (groupedByHeader) {
    const groupBy = groupedByHeader.dataPath;
    const groupedDataSource = dataItems.reduce(
      (groupings: FluidTableGroupedDataset[], current: FluidRowData) => {
        const groupIdx: number = groupings.findIndex(
          (group: FluidTableGroupedDataset) =>
            group.groupedByValue === current[groupBy]
        );
        if (groupIdx > -1) {
          groupings[groupIdx].groupedData.push(current);
        } else {
          groupings.push({
            groupedByValue: current[groupBy],
            groupByKey: groupBy,
            groupedData: [current],
            groupConfig: groupedByHeader.grouping,
          });
        }
        return groupings;
      },
      []
    );
    return prepareGroupHeaders(headers, groupedDataSource);
  }
  Utils.logPerformance('groupDataSource', start);
  return [{ groupedData: dataItems } as FluidTableGroupedDataset];
};

export const prepareGroupHeaders = (
  headers: FluidTableHeader[],
  groups: FluidTableGroupedDataset[]
) => {
  const isSorted = headers.find((h: FluidTableHeader) => h.grouping?.sorted);
  const withHeaders = groups.map(
    (group: FluidTableGroupedDataset, idx: number) =>
      ({
        ...group,
        groupHeader: groupHeader(headers, group, idx),
      } as FluidTableGroupedDataset)
  );
  return isSorted
    ? sortByGroupHeaderValue(
        withHeaders,
        isSorted.dataPath,
        isSorted.grouping.sortDirection
      )
    : withHeaders;
};

export const includeRowActions = (
  rowActions: FluidTableAction[] = [],
  disableColumnVisibility
) => {
  return (
    rowActions?.length > 0 ||
    ((rowActions || rowActions?.length === 0) && !disableColumnVisibility)
  );
};
